
import { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import Filter from '@/interfaces/filter.interface';
import tracking from '@/functions/tracking';
import mRangeSlider from '../../molecules/form/RangeSlider.vue';
import mTag from '../../molecules/form/Tag.vue';
import mSearchTags from '@/components/organisms/search/SearchTags.vue';
import { EventBus } from '../../../functions/eventBus';
import Format from '../../../functions/format';
import { SearchFilter, FilterGroup } from '../../../models/searchFilters.model';
import { SearchFilterTexts } from '@/interfaces/searchTexts.interface';
import { ZIP_REGEX } from '@/constants/regex.const';

interface Data {
    currentDirection: string;
    currentGroup: string | null;
    isDesktop: boolean;
    errorMessage: string;
}

export default {
    components: {
        mRangeSlider,
        mSearchTags,
        mTag,
    },

    props: {
        groups: {
            type: Array as PropType<FilterGroup[]>,
            default: () => [],
        },
        texts: {
            type: Object as PropType<SearchFilterTexts>,
            default: () => ({
                approve: '',
                reset: '',
                title: '',
            }),
        },
    },

    data(): Data {
        return {
            currentDirection: 'right',
            currentGroup: null,
            isDesktop: EventBus.isDesktop,
            errorMessage: '',
        };
    },

    computed: {
        ...mapGetters({
            filters: 'search/filters',
            order: 'search/order',
            modalType: 'modal/type',
        }),

        /**
         * Define the portal destination for the filters.
         *
         * @return {string}
         */
        destination(): string {
            return !this.isDesktop && this.showInModal
                ? 'modal'
                : 'searchFilters';
        },

        /**
         * Check if the store has any filters.
         *
         * @return {boolean}
         */
        hasFilters(): boolean {
            return this.filters.length > 0;
        },

        /**
         * Check if the modal should be shown.
         *
         * @return {boolean}
         */
        showInModal(): boolean {
            return this.modalType === 'filters';
        },
    },

    mounted(): void {
        EventBus.$on('app.click', () => {
            this.currentGroup = null;
        });

        EventBus.$on('app.resize', () => {
            this.isDesktop = EventBus.isDesktop;
        });
    },

    beforeUnmount(): void {
        EventBus.$off('app.click');
        EventBus.$off('app.resize');
    },

    methods: {
        ...mapActions({
            closeModal: 'modal/close',
            resetFilters: 'search/resetFilters',
            toggleFilter: 'search/toggleFilter',
        }),

        switchFilter(payload: Filter): void {
            this.toggleFilter({
                key: payload.key,
                label: payload.label,
                shortName: payload.shortName,
                value: payload.value,
            });

            tracking.track(
                'trackFilter',
                'Filtersoegning',
                `${payload.label} - Sortering: ${this.order}`,
                '',
            );
        },
        /**
         * Toggle active group.
         *
         * @param {string} value
         * @param {MouseEvent} event
         * @return {void}
         */
        toggleGroup(value: string, event: MouseEvent): void {
            if (this.currentGroup === value) {
                this.currentGroup = null;

                return;
            }

            this.currentGroup = value;

            let { left: containerLeft, right: containerRight } = (
                this.$refs.searchFilters as HTMLDivElement
            ).getBoundingClientRect();
            let { left: groupLeft, right: groupRight } = (event.target as any)
                .closest('.o-searchFilters__group')
                .getBoundingClientRect();
            let groupContentWidth = parseInt(
                window
                    .getComputedStyle(
                        (event.target as any)
                            .closest('.o-searchFilters__group')
                            .querySelector('.o-searchFilters__groupContent'),
                    )
                    .getPropertyValue('width'),
            );

            if (
                groupRight - groupContentWidth < containerLeft &&
                groupLeft + groupContentWidth > containerRight
            ) {
                this.currentDirection = 'center';
            } else if (
                groupLeft - containerLeft >
                containerRight - groupRight
            ) {
                this.currentDirection = 'left';
            } else {
                this.currentDirection = 'right';
            }
        },

        /**
         * Update a input filter.
         *
         * @param {Filter} filter
         * @param {string} value
         * @return {void}
         */
        updateInput(filter: SearchFilter, value: string): void {
            this.errorMessage = '';
            if (value.length >= 4) {
                if (filter.key.toLowerCase() === 'zipcode') {
                    const isMatch = ZIP_REGEX.test(value);
                    this.errorMessage = isMatch ? '' : 'Forkert postnummer!';
                }

                this.$store.dispatch('search/updateFilter', {
                    key: filter.key,
                    label: `${value}`,
                    shortName: filter.shortName,
                    value,
                });

                tracking.track(
                    'trackFilter',
                    'Filtersoegning',
                    filter.name,
                    `${value} - Sortering: ${this.order}`,
                );
            }
        },

        /**
         * Update a range filter.
         *
         * @param {Filter} filter
         * @param {[number, number]} value
         * @return {void}
         */
        updateRange(filter: SearchFilter, value: [number, number]): void {
            const prependValue =
                filter.min === value[0] && filter.prepend ? filter.prepend : '';
            const appendValue =
                filter.max === value[1] && filter.infinityIndicator ? '+' : '';
            const tagLabelValue = filter.tagLabel ? `${filter.tagLabel}:` : '';
            let label = '';

            if (filter.append === 'mio. kr.') {
                label = `${tagLabelValue} ${prependValue} ${Format.millions(value)}${appendValue} ${filter.append}`;
            } else if (filter.append === 'm2') {
                label = `${tagLabelValue} ${prependValue} ${Format.amount(value[0])}-${Format.amount(value[1])}${appendValue} m<sup>2</sup>`;
            } else if (filter.append) {
                label = `${tagLabelValue} ${prependValue} ${Format.amount(value[0])}-${Format.amount(value[1])}${appendValue} ${filter.append}`;
            } else {
                label = `${tagLabelValue} ${prependValue} ${value[0]}-${value[1]}${appendValue}`;
            }

            const trackingLabel = label.replace(tagLabelValue, '').trim();

            if (value[0] === filter.min && value[1] === filter.max) {
                this.$store.dispatch('search/removeFilter', {
                    key: filter.key,
                    shortName: filter.shortName,
                });

                tracking.track(
                    'trackFilter',
                    'Filtersoegning',
                    filter.name,
                    `${trackingLabel} - Sortering: ${this.order}`,
                );
                return;
            }

            this.$store.dispatch('search/updateFilter', {
                key: filter.key,
                label,
                shortName: filter.shortName,
                value,
            });

            tracking.track(
                'trackFilter',
                'Filtersoegning',
                filter.name,
                `${trackingLabel} - Sortering: ${this.order}`,
            );
        },

        /**
         * Update a value map range filter.
         *
         * @param {Filter} filter
         * @param {[number, number]} value
         * @return {void}
         */
        updateValueMapRange(
            filter: SearchFilter,
            value: [number, number],
        ): void {
            if (value[0] === filter.min && value[1] === filter.max) {
                this.$store.dispatch('search/removeFilter', {
                    key: filter.key,
                    shortName: filter.shortName,
                });

                tracking.track(
                    'trackFilter',
                    'Filtersoegning',
                    filter.name,
                    `${filter.valueMap[value[0]]}-${filter.valueMap[value[1]]} - Sortering: ${this.order}`,
                );
                return;
            }

            const tagLabelValue = filter.tagLabel ? `${filter.tagLabel}:` : '';

            this.$store.dispatch('search/updateFilter', {
                key: filter.key,
                label: `${tagLabelValue} ${filter.valueMap[value[0]]}-${filter.valueMap[value[1]]}`,
                shortName: filter.shortName,
                value,
            });

            tracking.track(
                'trackFilter',
                'Filtersoegning',
                filter.name,
                `${filter.valueMap[value[0]]}-${filter.valueMap[value[1]]} - Sortering: ${this.order}`,
            );
        },
    },
};
